/**
 * The panel's ▶ — ADR-033 phase 5, headless.
 *
 * Between the gesture and the stage there are two waits: the bytes (a fetch,
 * or a folder read) and the phase-4 door's own load. A gesture that arrives
 * while an earlier one is still waiting must win: each `play` takes a
 * generation at the GESTURE (the #140 rule — an opaque id a later gesture
 * invalidates), and a request whose generation is no longer current writes
 * nothing after either wait, reports nothing, and says it was superseded
 * (Codex P2, #152 round two: the byte resolution had no token of its own,
 * so a slow fetch could restart a stopped preview).
 *
 * Bytes are memoized PER LIBRARY READ AND FAMILY: the same id resolves to
 * the same buffer while the read stands (no second fetch per ▶), and a new
 * read — a bake folder opened, a re-read — names new bytes, which the
 * phase-4 door rebinds because the buffer differs (its cache remembers the
 * bytes a binding came from). So "the library changed" needs no clearing
 * call: the read's identity IS the version (Codex P2, #152 round two). The
 * family is in the key because one read can hold two pools (prod's family
 * and a foreign bake's), and a subject of the other family asking for the
 * same id must not get the first family's GLB (Codex P2, #152 round three).
 *
 * The SUBJECT is snapshotted at the gesture too: a character replaced or
 * grafted while the bytes are in flight clears the shell's preview but
 * presses no Stop, so the pending request would otherwise land its clip on
 * the replacement (Codex P2, #152 round three).
 *
 * Depends on: library-read / library-clip (types). Depended on by: main,
 * tests.
 */
import type { ResolvedClipBytes } from './library-clip.js';
import type { LibraryForRig, LibraryRead } from './library-read.js';

export type PlayOutcome = 'played' | 'stopped' | 'superseded' | 'refused';

export interface LibraryPlayerDeps {
  /** The library as read now; null = not read yet. */
  readonly read: () => LibraryRead | null;
  /** The rig's pool over a read, or why there is none. */
  readonly rig: (read: LibraryRead) => { rig: LibraryForRig } | { rig: null; reason: string };
  /** The subject's identity now (the scene); a request whose subject changed while waiting writes nothing. */
  readonly subject: () => object;
  /** Where the bytes come from (folder, then prod) — `resolveClipBytes` in the app. */
  readonly resolve: (inputs: { read: LibraryRead; rig: LibraryForRig; clipId: string }) => Promise<ResolvedClipBytes>;
  /** The phase-4 door: null stops. */
  readonly preview: (clipId: string | null, bytes?: ArrayBuffer) => Promise<unknown>;
  /** A refusal reaches the eye; a superseded request's failure does not (its gesture is gone). */
  readonly refused: (err: unknown) => void;
}

export interface LibraryPlayer {
  play(clipId: string | null): Promise<PlayOutcome>;
}

export function createLibraryPlayer(deps: LibraryPlayerDeps): LibraryPlayer {
  let generation = 0;
  const bytesByRead = new WeakMap<LibraryRead, Map<string, Promise<ResolvedClipBytes>>>();

  const resolveMemo = (read: LibraryRead, rig: LibraryForRig, clipId: string): Promise<ResolvedClipBytes> => {
    let forRead = bytesByRead.get(read);
    if (forRead === undefined) {
      forRead = new Map();
      bytesByRead.set(read, forRead);
    }
    const key = `${rig.detection.family?.id ?? ''}/${clipId}`;
    const held = forRead.get(key);
    if (held !== undefined) return held;
    const pending = deps.resolve({ read, rig, clipId });
    forRead.set(key, pending);
    // A failed resolution is not remembered — the next ▶ asks again (prod may be back).
    pending.catch(() => {
      if (forRead.get(key) === pending) forRead.delete(key);
    });
    return pending;
  };

  return {
    async play(clipId) {
      const mine = ++generation;
      if (clipId === null) {
        await deps.preview(null);
        return 'stopped';
      }
      const subject = deps.subject();
      try {
        const read = deps.read();
        if (read === null) throw new Error('library not read yet');
        const pool = deps.rig(read);
        if (pool.rig === null) throw new Error(pool.reason);
        const resolved = await resolveMemo(read, pool.rig, clipId);
        // The read captured at the gesture must still be THE read: a bake folder
        // opened meanwhile names another pool (Codex P2, #152 round four).
        if (mine !== generation || deps.subject() !== subject || deps.read() !== read) return 'superseded';
        await deps.preview(clipId, resolved.bytes);
        return mine === generation ? 'played' : 'superseded';
      } catch (err) {
        if (mine !== generation) return 'superseded';
        deps.refused(err);
        return 'refused';
      }
    },
  };
}
